import { useState, useEffect, useCallback }        from "react";
import { getCurrentUser, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import secureAuthService from '../services/secureAuthService';
import s3Service from '../services/s3Service';
import LoginPage           from "./LoginPage";
import ForgotPasswordPage  from "./ForgotPasswordPage";
import Sidebar             from "../components/Sidebar";
import HomePage            from "./HomePage";
import SleepDiaryPage      from "./SleepDiaryPage";
import GaitPage            from "./GaitPage";
import ADLSPage            from "./ADLSPage";
import SeniorsPage         from "./SeniorsPage";
import ResidentDetailsPage from "./ResidentDetailsPage";
import CaregiversPage      from "./CaregiversPage";
import CaregiverDetailsPage from "./CaregiverDetailsPage";
import AddSeniorPage from "./AddSeniorPage";
import AddCaregiverPage from "./AddCaregiverPage";
import EditCaregiverModal from "../components/EditCaregiverModal";
import EditResidentModal from "../components/EditResidentModal";
import FallsPage from "./FallsPage";
import FacilityGaitPage from "./FacilityGaitPage";
import FacilitySleepPage from "./FacilitySleepPage";
import useWindowSize from "../hooks/useWindowSize";
import SecureContent from "../components/SecureContent";
import comprehensiveSecurity from "../utils/comprehensiveSecurity";

// HIPAA Compliance Features
import encryptionService from "../utils/encryptionService";
import inactivityManager from "../utils/inactivityManager";

const PAGE_BG = {
  "Home":        "linear-gradient(160deg, #b8d4f0 0%, #9ec4ea 30%, #7daed8 60%, #6a9ccc 100%)",
  "Sleep Diary": "linear-gradient(160deg, #b8d4f0 0%, #9ec4ea 30%, #7daed8 60%, #6a9ccc 100%)",
  "Gait":        "linear-gradient(160deg, #b8d4f0 0%, #9ec4ea 30%, #7daed8 60%, #6a9ccc 100%)",
  "ADLs":        "linear-gradient(160deg, #b8d4f0 0%, #9ec4ea 30%, #7daed8 60%, #6a9ccc 100%)",
  "Seniors":     "linear-gradient(160deg, #b8d4f0 0%, #9ec4ea 30%, #7daed8 60%, #6a9ccc 100%)",
  "Caregivers":  "linear-gradient(160deg, #b8d4f0 0%, #9ec4ea 30%, #7daed8 60%, #6a9ccc 100%)",
  "Falls":       "linear-gradient(160deg, #b8d4f0 0%, #9ec4ea 30%, #7daed8 60%, #6a9ccc 100%)",
};

const ComingSoon = ({ name }) => (
  <main style={{
    flex: 1, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: 16,
  }}>
    <div style={{
      width: 64, height: 64, borderRadius: 16,
      background: "rgba(26,120,200,0.15)",
      border: "1px solid rgba(26,120,200,0.3)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
           stroke="rgba(255,255,255,0.5)" strokeWidth="1.6" strokeLinecap="round">
        <rect x="3" y="3" width="18" height="18" rx="3"/>
        <path d="M9 9h6M9 12h6M9 15h4"/>
      </svg>
    </div>
        <p style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{name}</p>
    <p style={{ margin: 0, fontSize: 14, opacity: 0.35 }}>
      This page is coming soon
    </p>
  </main>
);

export default function Dashboard() {
  const { isMobile } = useWindowSize();
  
  // State declarations - declare all state first
    const [user, setUser] = useState(null);
  const [authScreen, setAuthScreen] = useState("login"); // "login" | "forgot"
  const [activePage, setActivePage] = useState("Home");
  const [authLoading, setAuthLoading] = useState(true); // Check auth status on load
  const [seniorsFilters, setSeniorsFilters] = useState(null);
  const [caregiversFilters, setCaregiversFilters] = useState(null);
  const [fallsFilters, setFallsFilters] = useState(null);
  const [selectedResident, setSelectedResident] = useState(null);
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  const [caregiverBackContext, setCaregiverBackContext] = useState(null); // Track where to go back from caregiver details
  const [residentPageContext, setResidentPageContext] = useState(null); // Tracks which resident's page to show
  const [isInResidentContext, setIsInResidentContext] = useState(false); // Lock navigation when viewing resident-specific pages
  const [showAddSenior, setShowAddSenior] = useState(false); // Show Add Senior page
  const [showAddCaregiver, setShowAddCaregiver] = useState(false); // Show Add Caregiver page
  const [editingCaregiver, setEditingCaregiver] = useState(null); // Caregiver being edited
  const [editingResident, setEditingResident] = useState(null); // Resident being edited
    const [selectedResidentIdForPages, setSelectedResidentIdForPages] = useState(null); // Selected resident for Falls/Sleep/Gait/ADLs pages
  const [caregiversRefreshTrigger, setCaregiversRefreshTrigger] = useState(0); // Trigger to refresh caregivers list
  const [seniorsRefreshTrigger, setSeniorsRefreshTrigger] = useState(0); // Trigger to refresh seniors list
  const [isViewAllMode, setIsViewAllMode] = useState(false); // Track if Falls page is in View All mode

        // Check if user is already authenticated on mount
        const checkAuthStatus = useCallback(async () => {
          try {
            const sessionCheck = await secureAuthService.verifySession();
      
            if (!sessionCheck.authenticated) {
              setUser(null);
              setAuthLoading(false);
              return;
            }

            try {
              const currentUser = await getCurrentUser();
              await fetchAuthSession({ forceRefresh: true });
              const attributes = await fetchUserAttributes();
              const userEmail = attributes.email || currentUser.signInDetails?.loginId || currentUser.username;
              const fullName = attributes.name || currentUser.username;
  
              const userData = {
                email: userEmail,
                name: fullName,
                userId: currentUser.userId,
                username: currentUser.username,
                phoneNumber: attributes.phone_number || '',
                gender: attributes.gender || '',
                zoneinfo: attributes.zoneinfo || '',
                picture: attributes.picture || '',
                facilityId: attributes['custom:facility_id'] || '',
                facilityName: attributes['custom:facility_name'] || '',
                address1: attributes['custom:Address_1'] || '',
                address2: attributes['custom:Address_2'] || '',
                city: attributes['custom:City'] || '',
                state: attributes['custom:State'] || '',
                country: attributes['custom:Country'] || '',
              };
  
              setUser(userData);
              fetchUserPhotoFromS3(attributes);
            } catch (cognitoError) {
              setUser({
                email: sessionCheck.user?.email || 'user@example.com',
                name: sessionCheck.user?.email?.split('@')[0] || 'User',
                facilityId: sessionCheck.user?.facility_id || ''
              });
            }
          } catch (error) {
            setUser(null);
          } finally {
            setAuthLoading(false);
          }
        }, []);

        useEffect(() => {
      checkAuthStatus();
    }, [checkAuthStatus]);

    // Initialize HIPAA-compliant security features when user logs in
    useEffect(() => {
      if (user && !authLoading) {
        encryptionService.initialize().catch(() => {});

        inactivityManager.initialize(() => {
          handleSignOut();
        });

        comprehensiveSecurity.initialize({
          userId: user.email || user.username,
          userEmail: user.email,
          facilityName: user.facilityId || 'FallVision Healthcare',
          enableWatermark: true,
          enableSessionSecurity: false,
          enableDataProtection: false,
          enableNetworkSecurity: false,
          showSecurityAgreement: false
        });

        return () => {
          encryptionService.cleanup();
          inactivityManager.cleanup();
          comprehensiveSecurity.cleanup();
        };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authLoading]);

        const fetchUserPhotoFromS3 = async (attributes) => {
      try {
                const facilityId = attributes['custom:facility_id'];
        const userId = attributes.sub;
        
        if (!facilityId) {
          return;
        }
        
        // Try admin photo first: FAC-XXXX/admins/avatar.jpg
        let s3Key = `${facilityId}/admins/avatar.jpg`;
        let photoUrl = null;
        
                
        try {
          photoUrl = await s3Service.getPhotoUrl(s3Key);
        } catch (error) {
          // If admin photo not found, try caregiver photo: FAC-XXXX/caregivers/{user_id}.jpg
          s3Key = `${facilityId}/caregivers/${userId}.jpg`;
          
                    
          try {
            photoUrl = await s3Service.getPhotoUrl(s3Key);
          } catch (error2) {
            // No photo found - silent fail
          }
        }
        
        if (photoUrl) {
          // Update user state with client-side generated presigned URL
          setUser(prev => ({
            ...prev,
            picture: photoUrl
          }));
        }
      } catch (error) {
        console.error('[Dashboard] Error fetching user photo from S3:', error);
            }
    };

        const handleSignOut = async () => {
      try {
        // Use secure auth service to sign out (clears HTTP-only cookies)
        await secureAuthService.signOut();
      
        // Cleanup security services
        encryptionService.cleanup();
        inactivityManager.cleanup();
        comprehensiveSecurity.cleanup();
      
        setUser(null);
        setActivePage("Home");
        // Reset all state
        setSelectedResident(null);
        setSelectedCaregiver(null);
        setShowAddSenior(false);
        setShowAddCaregiver(false);
      
        // Force reload to clear everything
        window.location.reload();
      } catch (error) {
        console.error('Error signing out:', error);
        // Force reload anyway
        window.location.reload();
      }
    };

  /* ── Loading auth status ── */
  if (authLoading) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: "linear-gradient(160deg, #f5f5f5 0%, #eaeaea 100%)",
        fontFamily: "'Segoe UI', sans-serif",
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 50,
            height: 50,
            border: "4px solid #f0b429",
            borderTopColor: "transparent",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 16px"
          }} />
          <p style={{ color: "#1f2937", fontSize: 16 }}>Loading...</p>
        </div>
      </div>
    );
  }

  /* ── Not logged in → show auth screens ── */
  if (!user) {
    if (authScreen === "forgot") {
      return <ForgotPasswordPage onBackToLogin={() => setAuthScreen("login")} />;
    }
    return (
      <LoginPage
        onLogin={(u) => setUser(u)}
        onForgotPassword={() => setAuthScreen("forgot")}
      />
    );
  }

                        /* ── Logged in → show Dashboard ── */
  // If Add Caregiver page is open, show it
  if (showAddCaregiver) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: PAGE_BG["Caregivers"] || "#060c16",
        fontFamily: "'Segoe UI', sans-serif",
        color: "#ffffff",
        overflow: "hidden",
        transition: "background 0.3s ease, color 0.3s ease",
        position: "relative",
      }}>
                                <Sidebar 
          activePage="Caregivers" 
          onNavigate={(page) => {
            setShowAddCaregiver(false);
            setActivePage(page);
          }}
          seniorsFilters={null}
          caregiversFilters={null}
          onSignOut={handleSignOut}
          user={user}
          selectedResidentId={selectedResidentIdForPages}
          onResidentSelect={setSelectedResidentIdForPages}
        />
                <AddCaregiverPage 
                  onBack={() => setShowAddCaregiver(false)}
                  onCaregiverAdded={(newCaregiver) => {
                    setShowAddCaregiver(false);
                    setCaregiversRefreshTrigger(prev => prev + 1);
                  }}
                  user={user}
                />
      </div>
    );
  }

  // If Add Senior page is open, show it
  if (showAddSenior) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: PAGE_BG["Seniors"] || "#060c16",
        fontFamily: "'Segoe UI', sans-serif",
        color: "#ffffff",
        overflow: "hidden",
        transition: "background 0.3s ease, color 0.3s ease",
        position: "relative",
      }}>
                                <Sidebar 
          activePage="Seniors" 
          onNavigate={(page) => {
            setShowAddSenior(false);
            setActivePage(page);
          }}
          seniorsFilters={null}
          caregiversFilters={null}
          onSignOut={handleSignOut}
          user={user}
          selectedResidentId={selectedResidentIdForPages}
          onResidentSelect={setSelectedResidentIdForPages}
        />
                <AddSeniorPage 
                  onBack={() => setShowAddSenior(false)}
                  onSeniorAdded={(newResident) => {
                    setShowAddSenior(false);
                    setSeniorsRefreshTrigger(prev => prev + 1);
                  }}
                  user={user}
                />
      </div>
    );
  }

        // If a caregiver is selected, show the caregiver details page
    if (selectedCaregiver) {
      // Determine back button text based on context
      let backButtonText = 'Back to Home';
      if (caregiverBackContext?.type === 'resident-details') {
        backButtonText = 'Back to Resident Details';
      } else if (caregiverBackContext === null && activePage === 'Caregivers') {
        backButtonText = 'Back to Caregivers';
      }
    
      return (
        <div style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          background: PAGE_BG[activePage] || "#060c16",
          fontFamily: "'Segoe UI', sans-serif",
          color: "#ffffff",
          overflow: "hidden",
          transition: "background 0.3s ease, color 0.3s ease",
          position: "relative",
        }}>
                                        <Sidebar 
            activePage={activePage} 
            onNavigate={setActivePage}
            seniorsFilters={activePage === "Seniors" ? seniorsFilters : null}
            caregiversFilters={activePage === "Caregivers" ? caregiversFilters : null}
            onSignOut={handleSignOut}
            user={user}
            selectedResidentId={selectedResidentIdForPages}
            onResidentSelect={setSelectedResidentIdForPages}
          />
                  <CaregiverDetailsPage 
            caregiverId={selectedCaregiver}
            backButtonText={backButtonText}
                        onBack={() => {
              // Context-aware back navigation
              if (caregiverBackContext?.type === 'resident-details') {
                // Go back to resident details
                const residentId = caregiverBackContext.residentId;
                setSelectedCaregiver(null);
                setCaregiverBackContext(null);
                setSelectedResident(residentId);
              } else if (caregiverBackContext === null && activePage === 'Caregivers') {
                // Go back to caregivers page
                setSelectedCaregiver(null);
                setCaregiverBackContext(null);
                setActivePage('Caregivers');
              } else {
                // Go back to home page
                setSelectedCaregiver(null);
                setCaregiverBackContext(null);
                setActivePage('Home');
              }
            }}
            onNavigateToScreen={(screen, data) => {
              // Handle navigation from caregiver details
              if (screen === 'resident-details') {
                // Navigate to resident details page
                // Store the current caregiver ID so we can come back
                const currentCaregiverId = selectedCaregiver;
                setSelectedCaregiver(null);
                setSelectedResident(data); // data is the resident ID
                // Set back context to remember we came from caregiver details
                setCaregiverBackContext({
                  type: 'caregiver-details',
                  caregiverId: currentCaregiverId
                });
              } else {
                setSelectedCaregiver(null);
                setCaregiverBackContext(null);
                setActivePage(screen);
              }
            }}
          />
        </div>
      );
    }
  
        // If a resident is selected, show the details page
  if (selectedResident) {
    // Determine back button text based on where we came from
    let backButtonText = 'Back to Home';
    if (activePage === 'Seniors') {
      backButtonText = 'Back to Seniors';
    } else if (caregiverBackContext?.type === 'caregiver-details') {
      backButtonText = 'Back to Caregiver Details';
    }
    
    return (
            <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: PAGE_BG[activePage] || "#060c16",
        fontFamily: "'Segoe UI', sans-serif",
        color: "#ffffff",
        overflow: "hidden",
        transition: "background 0.3s ease, color 0.3s ease",
        position: "relative",
      }}>
                                <Sidebar 
          activePage={activePage} 
          onNavigate={setActivePage}
          seniorsFilters={activePage === "Seniors" ? seniorsFilters : null}
          caregiversFilters={activePage === "Caregivers" ? caregiversFilters : null}
          onSignOut={handleSignOut}
          user={user}
          selectedResidentId={selectedResidentIdForPages}
          onResidentSelect={setSelectedResidentIdForPages}
        />
                                                                <ResidentDetailsPage 
          residentId={selectedResident}
          backButtonText={backButtonText}
                    onBack={() => {
            setSelectedResident(null);
            setIsInResidentContext(false);
            setResidentPageContext(null);
            // Go back to the page we came from
            if (caregiverBackContext?.type === 'caregiver-details') {
              // Go back to caregiver details
              const caregiverId = caregiverBackContext.caregiverId;
              setCaregiverBackContext(null);
              setSelectedCaregiver(caregiverId);
            } else if (activePage !== 'Seniors') {
              setActivePage('Home');
            }
          }}
                    onNavigateToScreen={(screen, data) => {
                      // When clicking arrow on resident details, navigate with context lock
                      if (screen === 'Caregivers') {
                        // Navigate to caregiver details with back context
                        setSelectedResident(null);
                        setSelectedCaregiver(data); // data is the caregiver ID
                        setCaregiverBackContext({
                          type: 'resident-details',
                          residentId: selectedResident
                        });
                      } else {
                        setSelectedResident(null);
                        setActivePage(screen);
                        setResidentPageContext(data);
                        setIsInResidentContext(true); // Lock navigation
                      }
                    }}
        />
      </div>
    );
  }
  
                        // Render current page based on activePage
  let CurrentPage;
  
  // Function to go back to resident details from context pages
  const handleBackToResidentDetails = () => {
    const resId = residentPageContext;
    setResidentPageContext(null);
    setIsInResidentContext(false);
    setSelectedResident(resId); // Show the resident details page again
  };
  
    // Handler for navigating with resident context from Home page
    const handleNavigateWithResident = (page, residentId) => {
      setActivePage(page);
      setSelectedResidentIdForPages(residentId); // Set the resident ID for the page
      // Don't set isInResidentContext - we're not coming from Resident Details
    };
  
    if (activePage === "Home") {
    CurrentPage = <HomePage 
      onNavigate={setActivePage}
      onNavigateWithResident={handleNavigateWithResident}
      onResidentClick={(residentId) => setSelectedResident(residentId)}
      onCaregiverClick={(caregiverId) => {
        setSelectedCaregiver(caregiverId);
        setCaregiverBackContext(null); // No context, will go back to Home page
      }}
    />;
  } else if (activePage === "Sleep Diary") {
    const residentId = isInResidentContext ? residentPageContext : selectedResidentIdForPages;
    if (residentId) {
      CurrentPage = <SleepDiaryPage 
        residentId={residentId} 
        showBackButton={isInResidentContext}
        onBackToResident={handleBackToResidentDetails}
      />;
    } else {
      CurrentPage = <FacilitySleepPage 
        onResidentClick={(residentId) => setSelectedResidentIdForPages(residentId)}
      />;
    }
    } else if (activePage === "Gait") {
    const residentId = isInResidentContext ? residentPageContext : selectedResidentIdForPages;
    if (residentId) {
      // Individual resident gait page (detail view with charts)
            CurrentPage = <GaitPage 
        key={residentId}
        residentId={residentId}
        showBackButton={isInResidentContext}
        onBackToResident={handleBackToResidentDetails}
      />;
    } else {
      // Facility-level gait overview (no resident selected)
      CurrentPage = <FacilityGaitPage 
        onResidentClick={(residentId) => setSelectedResidentIdForPages(residentId)}
      />;
    }
    } else if (activePage === "ADLs") {
    const residentId = isInResidentContext ? residentPageContext : selectedResidentIdForPages;
    CurrentPage = <ADLSPage
      key={residentId}
      residentId={residentId}
      showBackButton={isInResidentContext}
      onBackToResident={handleBackToResidentDetails}
    />;
    } else if (activePage === "Falls") {
    const residentId = isInResidentContext ? residentPageContext : selectedResidentIdForPages;
    CurrentPage = <FallsPage 
      onFiltersChange={setFallsFilters}
      residentId={residentId}
      showBackButton={isInResidentContext}
      onBackToResident={handleBackToResidentDetails}
      onViewAllModeChange={setIsViewAllMode}
    />;
  } else if (activePage === "Seniors") {
                                CurrentPage = <SeniorsPage 
      onFiltersChange={setSeniorsFilters} 
      onResidentClick={(residentId) => setSelectedResident(residentId)}
      onAddSenior={() => setShowAddSenior(true)}
      onEditResident={(resident) => setEditingResident(resident)}
      refreshTrigger={seniorsRefreshTrigger}
    />;
            } else if (activePage === "Caregivers") {
            CurrentPage = <CaregiversPage 
      onFiltersChange={setCaregiversFilters} 
      onCaregiverClick={(caregiverId) => {
        setSelectedCaregiver(caregiverId);
        setCaregiverBackContext(null); // No context, will go back to Caregivers page
      }}
      onAddCaregiver={() => setShowAddCaregiver(true)}
      onEditCaregiver={(caregiver) => setEditingCaregiver(caregiver)}
      refreshTrigger={caregiversRefreshTrigger}
    />;
  } else {
    CurrentPage = <ComingSoon name={activePage} />;
  }
    const bg = PAGE_BG[activePage] || "#060c16";
  const textColor = "#ffffff";

    return (
        <div style={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      background: bg,
      fontFamily: "'Segoe UI', sans-serif",
      color: textColor,
      overflow: "hidden",
      transition: "background 0.3s ease, color 0.3s ease",
      position: "relative",
    }}>
                                                                                                                                                                                                                                                                                                <Sidebar 
        activePage={activePage} 
                onNavigate={(page) => {
          if (isInResidentContext && page !== "Seniors") {
            return;
          }
          if (page === "Seniors" && (residentPageContext || isInResidentContext)) {
            setResidentPageContext(null);
            setIsInResidentContext(false);
          }
          if (page !== "Falls" && isViewAllMode) {
            setIsViewAllMode(false);
          }
          setActivePage(page);
        }}
                seniorsFilters={activePage === "Seniors" ? seniorsFilters : null}
        caregiversFilters={activePage === "Caregivers" ? caregiversFilters : null}
        fallsFilters={activePage === "Falls" ? fallsFilters : null}
        isNavigationLocked={isInResidentContext}
        onSignOut={handleSignOut}
        user={user}
        selectedResidentId={selectedResidentIdForPages}
        onResidentSelect={setSelectedResidentIdForPages}
        isViewAllMode={isViewAllMode}
        onFacilityOverview={(activePage === "Gait" || activePage === "Sleep Diary") && selectedResidentIdForPages && !isInResidentContext ? () => setSelectedResidentIdForPages(null) : null}
            />
      
      {/* Wrap main content with security protection */}
      <SecureContent level="high" watermark={true}>
        {CurrentPage}
      </SecureContent>
      
            {/* Edit Caregiver Modal */}
      {editingCaregiver && (
        <EditCaregiverModal
          caregiver={editingCaregiver}
          onClose={() => setEditingCaregiver(null)}
          onUpdate={(updatedCaregiver) => {
            setEditingCaregiver(null);
            setCaregiversRefreshTrigger(prev => prev + 1);
          }}
          isMobile={isMobile}
        />
      )}
      
      {editingResident && (
        <EditResidentModal
          resident={editingResident}
          onClose={() => setEditingResident(null)}
          onUpdate={(updatedResident) => {
            setEditingResident(null);
            setSeniorsRefreshTrigger(prev => prev + 1);
          }}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}
